/*globals $:false */

import { DEV_MODE } from '../helpers/cob.devmode.js';
import tmpl_log_entry from '../templates/log-entry.dot';
import tmpl_log_entry_empty from '../templates/log-entry-empty.dot';
import tmpl_log_entry_loading from '../templates/log-entry-loading.dot';
import tmpl_log_entry_loading_error from '../templates/log-entry-loading-error.dot';
import tmpl_log_entry_more from '../templates/log-entry-more.dot';
import tmpl_log_value_link from '../templates/log-value-link.dot';
import tmpl_log_wrapper from '../templates/log-wrapper.dot';

import utils from '../cob.utils.js';

// `opts` is an options object with the following supported attributes:
// `opts.i18nMap` a map with the translations to be used (the map must be of the form {"LANG":{"KEY":"VALUE"}}
//                If this param is null then the translate will be as usual (cob.log_LANG.json files must be present in
//                the i18n directory of each app)
var LogPresenter = function(core, logWrapperDivSelector, opts) {
    opts = opts || {};

    if(opts.i18nMap){
        //translator needs a module name
        opts.i18nMap["cob.log"] = opts.i18nMap;
    }

    var _entityNameAndId, //context variable used to identify the presenter when there are simultaneous logPs in the same page
        _appInfoM = core.getAppInfoModel(),
        logModel = new LogModel(core, _appInfoM.appId.toLowerCase(), _appInfoM.appUrls.logm, 5),

        view = new LogView(core, logWrapperDivSelector,
            {
                moreLogs: function(mult) {
                    logModel.getNextPage(mult);
                }
            },
            opts
        );

    var _onLogListUpdate = function(ev, data) {
        //only respond to events associated with the current entity name and Id
        //this is specially important when there are simultaneous log presenters in a page
        if (data.entityNameAndId === _entityNameAndId) {
            view.removeNotifications();

            if (logModel.hasLogs()) {
                logModel.getResults().map(function(el) {
                    view.addLogEntry(el);
                });

                if (logModel.hasMorePages()) {
                    view.addMoreLogEntry();
                }

            } else {
                view.showEmptyLogEntry();
            }
        }
    };

    var _onLogLoadTimeout = function(ev, data) {
        //only respond to events associated with the current entityId
        //this is specially important when there are simultaneous log presenters in a page
        if (data.entityNameAndId === _entityNameAndId) {
            view.showErrorLoadingNotification();
        }
    };

    return{
        init: function(entityName, entityId) {
            _entityNameAndId = entityName + entityId;

            view.clearLogs();
            view.showLoadingNotification();
            logModel.resetModel(entityName, entityId);

            core.subscribe('log-list-updated', _onLogListUpdate);
            core.subscribe('error-loading-log-list', _onLogLoadTimeout);
        },
        pause: function() {
            core.unsubscribe('log-list-updated', _onLogListUpdate);
            core.unsubscribe('error-loading-log-list', _onLogLoadTimeout);
        },
        resume: function() {
            core.subscribe('log-list-updated', _onLogListUpdate);
            core.subscribe('error-loading-log-list', _onLogLoadTimeout);
        },
        refresh: function() {
            view.clearLogs();
            view.showLoadingNotification();
            logModel.refresh();
        },
        setLogPageSize: function(rowsPerPage) {
            logModel.setRowsPerPage(rowsPerPage);
        },
        clear: function() {
            view.clearLogs();
        },
        appendLogEntry: function(entity, entityId, comment) {
            //This function is used to append a new log entry without waiting for the server reply.

            view.appendLogEntry({
                user: _appInfoM.username,
                action: "usercomment",
                entity: entity,
                entity_id: entityId,
                timestamp: new Date().getTime(),
                message: comment
            });
        },
        removeFirstLogEntryWithComment: function(comment) {
            view.removeFirstLogEntryWithComment(comment);
        },
        stop: function() {
            view.destroy();

            core.unsubscribe('log-list-updated', _onLogListUpdate);
            core.unsubscribe('error-loading-log-list', _onLogLoadTimeout);
        }
    };
};

var LogModel = function(core, appName, baseLogMUrl, rowsPerPage) {
    this._core = core;
    this._appName = appName;
    this._baseLogMUrl = baseLogMUrl || core.getLogMUrl();
    this._rowsPerPage = rowsPerPage || 5;

    this._pageResults = [];
    this._lastTimestamp = undefined;
    this._lastLogId = undefined;
    this._has_more = false;

    this._entityName = '';
    this._entityId = '';
};
LogModel.prototype.setRowsPerPage = function(rowsPerPage) {
    this._rowsPerPage = rowsPerPage;
};
LogModel.prototype._buildUrl = function(mult) {
    return this._baseLogMUrl + '/logm/log/' + this._appName + '/' + this._entityName + '/' + this._entityId +
           '?timestamp=' + this._lastTimestamp +
           '&lastLogId=' + this._lastLogId +
           '&size=' + (( mult ?? 1 ) * this._rowsPerPage);
};
LogModel.prototype.resetModel = function(entityName, entityId) {
    this._pageResults = [];
    this._lastTimestamp = 0;
    this._lastLogId = 0;
    this._has_more = false;
    this._entityName = entityName;
    this._entityId = entityId;

    this.getNextPage();
};
LogModel.prototype.refresh = function() {
    this._lastTimestamp = 0;
    this._lastLogId = 0;
    this.getNextPage();
};
LogModel.prototype.getNextPage = function(mult) {
    var _this = this,
        url = _this._buildUrl(mult);

    this._pageResults = [];

    DEV_MODE && window.console.log("LOG:loading logs with", url);

    $.ajax({
        'type': 'GET',
        'url': url,
        ignoreErrors: true, // we don't want to show the forbidden page, only no logs
        xhrFields: {
            withCredentials: true
        },
        success: function(data) {
            if (data && data.entries) {
                data.entries.forEach(function(el) {
                    _this._pageResults.push(el);
                });

                _this._has_more = data.has_more;
                _this._lastTimestamp = data.last_timestamp;
                _this._lastLogId = data.last_id;
            }

            this._core.publish('log-list-updated', {entityNameAndId: _this._entityName + _this._entityId});
        }.bind(this),
        error: function(req) {
            window.console.log("LOG:error: " + req.status + " : " + req.statusText + ":" + req.responseText);
            this._core.publish('error-loading-log-list', {entityNameAndId: _this._entityName + _this._entityId});
        }.bind(this)
    });
};
LogModel.prototype.getResults = function() {
    return this._pageResults;
};
LogModel.prototype.hasMorePages = function() {
    return this._has_more;
};
LogModel.prototype.hasLogs = function() {
    return this._pageResults && this._pageResults.length > 0;
};

var LogView = function(core, logWrapperDivSelector, callbacks, opts) {
    var _$logDivWrapper = $(logWrapperDivSelector),
        maxLogFieldValueSize = 50,
        _$logList;

    _$logDivWrapper.append(tmpl_log_wrapper({}));

    _$logList = _$logDivWrapper.find("ul.history-items");

    var _translateDiffTitles = function(logEntry){
       var logModule = "cob.log",
           translationKey;

       if(logEntry.message){
           logEntry.message = logEntry.message.replace(new RegExp("__\\w+__", "g"), function(match){
               translationKey =  "log." + match;

               var translated = logEntry.i18nMap ?
                            core.translateStringFromMap(logEntry.i18nMap, logModule, translationKey) :
                            core.translateString(logModule, translationKey);

               //if we didn't find a translation, show the original
               return translated || match;
           });
       }
    };

    var _fillLocalDateTime = function(logEntry){
        logEntry.localDateTime = utils.time.getLocalDateTimeFromTimeStamp(logEntry.timestamp, core.getLocale());
    };

    var _showExpandableFieldDiffValues = function(logEntry){
        var $logEntryHtml = $(logEntry);

        //When a field value is changed the message should contain a <span class= 'js-big-log-entry'></span>
        //for each value that is larger than 50 characters
        $(".js-big-log-entry", $logEntryHtml).each(function(idx, entry){

            var $entry = $(entry),
                $moreLink = $(tmpl_log_value_link({
                    linkType: 'more-log-value',
                    link: logEntry.i18nMap ? core.translateStringFromMap(logEntry.i18nMap, 'cob.log', 'log.more-log-value')
                                           : core.translateString('cob.log', 'log.more-log-value')
                })),
                $lessLink = $(tmpl_log_value_link({
                    linkType: 'less-log-value',
                    link: logEntry.i18nMap ? core.translateStringFromMap(logEntry.i18nMap, 'cob.log', 'log.less-log-value')
                                           : core.translateString('cob.log', 'log.less-log-value')
                })),
                entryText = $entry.html(),
                entryMinText = entryText.substring(0, maxLogFieldValueSize);

            $entry.on("click", ".js-more-log-value-link", function(){
                $entry.html(entryText + " ");
                $entry.append($lessLink);
            });

            $entry.on("click", ".js-less-log-value-link", function(){
                $entry.html(entryMinText + "... ");
                $entry.append($moreLink);
            });

            $entry.html(entryMinText + "... ");
            $entry.append($moreLink);
        });
    };

    var _addLogEntryAuthorTooltip = function(logEntry){
        var $entry,
            $entryUsername;

        $(".log-author", logEntry).each(function(idx, entry){
            $entry = $(entry);
            $entryUsername = $entry.attr("data-username");

            $entry.tooltip({
                trigger: 'hover',
                delay: { show: 0, hide: 0},
                "original-title": $entryUsername,
                "title": $entryUsername
            });
        });
    };

    return{
        clearLogs: function() {
            _$logList.empty();
        },
        addLogEntry: function(logEntry) {
            var $logEntry;

            logEntry.i18nMap = opts.i18nMap;
            logEntry.skipLocalize = opts.skipLocalize;

            // gives a chance to consumers of the lib to modify instances on-the-fly
            // used first by RecordM to migrate to json diffs
            if(opts.prepare && typeof opts.prepare == 'function'){
                opts.prepare(logEntry);
            }

            _translateDiffTitles(logEntry);
            _fillLocalDateTime(logEntry);

            $logEntry = $(tmpl_log_entry({
               logEntry: logEntry,
               translate: core.translateStringFromMap
            }));

            _showExpandableFieldDiffValues($logEntry);

            _addLogEntryAuthorTooltip($logEntry);

            _$logList.append($logEntry);

            if (!opts.i18nMap) {
                core.translate('cob.log', ".log-entry");
            }

        },
        appendLogEntry: function(logEntry) {
            //This function is used to append a new log entry without waiting for the server reply.
            logEntry.i18nMap = opts.i18nMap;

            _fillLocalDateTime(logEntry);

            _$logList.prepend(tmpl_log_entry({
               logEntry: logEntry,
               translate: core.translateStringFromMap
            }));
            if(!opts.i18nMap){
                core.translate('cob.log', ".log-entry");
            }
        },
        removeFirstLogEntryWithComment: function(comment) {
            var logEntries = $(".log-entry", _$logList),
                entryMessage;

            //We only remove the first log entry with the given comment
            for (var index = 0; index < logEntries.length; index++) {
                entryMessage = $(".log-message", logEntries[index]);

                if (entryMessage.html() === comment) {
                    logEntries[index].remove();
                    break;
                }
            }
        },
        showEmptyLogEntry: function() {
            _$logList.html(tmpl_log_entry_empty({
               msg: opts.i18nMap ? core.translateStringFromMap(opts.i18nMap, 'cob.log', 'log.empty')
                                      : core.translateString('cob.log', 'log.empty')
            }));
        },
        addMoreLogEntry: function() {
            var _$moreLogBtn = $(tmpl_log_entry_more({
               more: opts.i18nMap ? core.translateStringFromMap(opts.i18nMap, 'cob.log', 'log.more')
                  : undefined
            }));
            _$logList.append(_$moreLogBtn);

            if(!opts.i18nMap){
                core.translate('cob.log', ".log-entry");
            }

            _$moreLogBtn.click(function(event) {
                _$moreLogBtn.remove();
                callbacks.moreLogs(event.altKey ? 10 : 1);
            });
        },
        showLoadingNotification: function() {
            _$logList.html(tmpl_log_entry_loading({
               loading: opts.i18nMap ? core.translateStringFromMap(opts.i18nMap, 'cob.log', 'log.loading')
                  : undefined
            }));
            if(!opts.i18nMap){
                core.translate('cob.log', ".log-entry");
            }
        },
        showErrorLoadingNotification: function() {
            _$logList.html(tmpl_log_entry_loading_error({
               msg: (opts.i18nMap
                  ? core.translateStringFromMap(opts.i18nMap, "cob.log", "log.error-loading")
                  : "Couldn't load the logs")
            }));
            if(!opts.i18nMap){
                core.translate('cob.log', ".log-entry");
            }
        },
        removeNotifications: function() {
            var _banner = _$logList.find('.notification');
            if (_banner) _banner.remove();
        },
        destroy: function() {
            //Remove all click event handlers from log entries
            $(".js-big-log-entry").off("click");

            _$logDivWrapper.html('');
        }
    };
};

export default LogPresenter;
